import { getAppRouteMeta } from '@/routing/router-static-data';
import type { AppRouteMeta } from '@/routing/route-meta';

export type RouterNavEntry = {
  id: string;
  fullPath: string;
  meta: AppRouteMeta;
};

/**
 * A minimal route interface for navigation extraction.
 * Using this instead of AnyRoute avoids complex type variance issues
 * with TanStack Router's strict generics.
 */
type RouteForNav = {
  id?: unknown;
  fullPath?: unknown;
  options?: { staticData?: unknown };
  children?: unknown;
};

function isRouteForNav(value: unknown): value is RouteForNav {
  return typeof value === 'object' && value !== null;
}

/**
 * Maps a single route to a nav entry, or returns null if it doesn't declare a
 * visible nav label.
 */
function toNavEntry(route: RouteForNav): RouterNavEntry | null {
  const meta = getAppRouteMeta(route);

  if (
    meta?.nav?.label &&
    !meta.nav.hidden &&
    'id' in route &&
    'fullPath' in route &&
    typeof route.id === 'string' &&
    typeof route.fullPath === 'string'
  ) {
    return { id: route.id, fullPath: route.fullPath, meta };
  }

  return null;
}

/**
 * Collects nav entries from a flat list of routes (e.g. `router.flatRoutes`).
 *
 * Preferred over {@link extractNavEntriesFromRouteTree}: the router instance's
 * flattened routes are processed once at `router.init()` and remain stable
 * across HMR, whereas the raw `routeTree` export can lose its assembled
 * `children` when an importing module is hot-reloaded.
 */
export function extractNavEntriesFromRoutes(
  routes: readonly RouteForNav[],
): RouterNavEntry[] {
  const out: RouterNavEntry[] = [];
  for (const route of routes) {
    const entry = toNavEntry(route);
    if (entry) out.push(entry);
  }
  return out;
}

function prettifyPath(fullPath: string): string {
  return (
    fullPath
      .split('/')
      .filter((seg) => seg && !seg.startsWith('$'))
      .map((seg) =>
        seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      )
      .join(' ') || fullPath
  );
}

export type NavigableEntry = {
  id: string;
  fullPath: string;
  meta: AppRouteMeta;
  /** Display label: the route's own nav label, else a prettified path. */
  label: string;
  /** True when the route declares an explicit nav label (a curated sidebar item). */
  hasNavLabel: boolean;
};

/**
 * Every route a user could be sent to directly — the "anywhere you can go" set,
 * used by Nami's navigate_to_page whitelist and home "jump to" grid. Unlike
 * {@link extractNavEntriesFromRoutes}, this does NOT require a `nav.label`, so
 * pages deliberately kept out of the sidebar (e.g. the admin-only `/workflows`)
 * are still navigable. Labels fall back to a prettified path. Callers must
 * still filter by `canAccessAppRouteMeta`; route `beforeLoad` guards remain the
 * real access gate.
 *
 * Param routes (`/x/$id`) and the root are skipped — they need a concrete
 * target — and entries are deduped by path, preferring one with a nav label.
 */
export function extractNavigableEntries(
  routes: readonly RouteForNav[],
): NavigableEntry[] {
  const byPath = new Map<string, NavigableEntry>();
  for (const route of routes) {
    if (
      !('id' in route) ||
      typeof route.id !== 'string' ||
      !('fullPath' in route) ||
      typeof route.fullPath !== 'string'
    ) {
      continue;
    }
    const fullPath = route.fullPath;
    if (
      !fullPath.startsWith('/') ||
      fullPath === '/' ||
      fullPath.includes('$')
    ) {
      continue;
    }
    // A route with no `staticData.app` declares no access rules here — its
    // guards live only in `beforeLoad` — so `canAccessAppRouteMeta` can't vet
    // it and would wrongly pass. Exclude it rather than surface a
    // guarded/internal route (e.g. /settings/tenant) in Nami's catalog.
    const meta = getAppRouteMeta(route);
    if (!meta) continue;
    if (meta.nav?.hidden) continue;

    const hasNavLabel = Boolean(meta.nav?.label);
    const label = meta.nav?.label ?? prettifyPath(fullPath);
    const existing = byPath.get(fullPath);
    // Dedupe (index routes share their layout's path); keep an explicitly
    // labelled entry over a derived one.
    if (!existing || (!existing.hasNavLabel && hasNavLabel)) {
      byPath.set(fullPath, {
        id: route.id,
        fullPath,
        meta,
        label,
        hasNavLabel,
      });
    }
  }
  return Array.from(byPath.values());
}

function getChildren(route: RouteForNav): RouteForNav[] {
  const { children } = route;
  if (!children) return [];

  if (Array.isArray(children)) {
    return children.filter(isRouteForNav);
  }

  if (typeof children === 'object') {
    return Object.values(children).filter(isRouteForNav);
  }

  return [];
}

/**
 * Collects all routes in the route tree that declare `staticData.app.nav.label`.
 *
 * This is Phase 2 groundwork to generate sidebar navigation from TanStack Router.
 */
export function extractNavEntriesFromRouteTree(
  routeTree: RouteForNav,
): RouterNavEntry[] {
  const out: RouterNavEntry[] = [];

  const visit = (route: RouteForNav) => {
    const entry = toNavEntry(route);
    if (entry) out.push(entry);

    for (const child of getChildren(route)) {
      visit(child);
    }
  };

  visit(routeTree);

  return out;
}

export type SidebarNavGroup = {
  /**
   * Stable identity for React keys and collapse state. For custom layouts this
   * is the persisted section id; for the default route-derived grouping it is
   * the group title (unique, since groups are keyed by title here).
   */
  id: string;
  title: string;
  items: RouterNavEntry[];
};

/**
 * Groups entries by `meta.nav.group`; items within a group are sorted
 * alphabetically by label. (`meta.nav.order` no longer drives item order —
 * alphabetical keeps the sidebar predictable as pages are added.)
 */
export function groupNavEntries(entries: RouterNavEntry[]): SidebarNavGroup[] {
  const byGroup = new Map<string, RouterNavEntry[]>();

  for (const entry of entries) {
    const group = entry.meta.nav?.group ?? 'Other';
    const arr = byGroup.get(group) ?? [];
    arr.push(entry);
    byGroup.set(group, arr);
  }

  const groups: SidebarNavGroup[] = Array.from(byGroup.entries()).map(
    ([title, items]) => ({
      id: title,
      title,
      items: items
        .slice()
        .sort((a, b) =>
          (a.meta.nav?.label ?? '').localeCompare(b.meta.nav?.label ?? ''),
        ),
    }),
  );

  // Sort groups by groupOrder (derived from first item), then alphabetically
  groups.sort((a, b) => {
    const ao = a.items[0]?.meta.nav?.groupOrder ?? Infinity;
    const bo = b.items[0]?.meta.nav?.groupOrder ?? Infinity;
    if (ao !== bo) return ao - bo;
    return a.title.localeCompare(b.title);
  });

  return groups;
}
